import { Link } from "wouter";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { ApiError } from "@workspace/api-client-react";

function isAuthErrorLike(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

interface BillingSummary {
  hasPlan: boolean;
  creditsUsed?: number;
  totalQuota?: number;
  remaining?: number;
  usagePercent?: number;
  boosterCreditsAvailable?: number;
}

function formatNumber(n: number | undefined | null): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "0";
  return Math.round(v).toLocaleString();
}

/**
 * Credit usage counter shown in the sidebar footer. Fetches the
 * authenticated user's current billing-cycle usage summary and renders
 * a compact "used / quota" badge that links to /billing.
 */
export function CreditUsageCounter({ variant = "sidebar" }: { variant?: "sidebar" | "top" | "mobile-compact" }) {
  const BASE = import.meta.env.BASE_URL || "/";
  // Task #734 — defer this query until the page is interactive so it
  // doesn't pile onto the Plan Content posts fetch on every layout
  // mount. The counter is non-critical for first paint; an empty
  // placeholder for ~1s is fine.
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    if (typeof w.requestIdleCallback === "function") {
      const handle = w.requestIdleCallback(() => setEnabled(true), { timeout: 2000 });
      return () => {
        if (typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(handle);
      };
    }
    const t = window.setTimeout(() => setEnabled(true), 1500);
    return () => window.clearTimeout(t);
  }, []);
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<BillingSummary>({
    queryKey: ["billing", "summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}api/billing/summary`, { credentials: "include" });
      if (!res.ok) {
        // Surface 401 as an ApiError so the global query-cache handler
        // (App.tsx → handleSessionExpired) can detect the expired session
        // and route the user to /login instead of leaving the badge in a
        // stuck "Unavailable" state.
        if (res.status === 401 || res.status === 403) {
          throw new ApiError(res, null, { method: "GET", url: res.url });
        }
        throw new Error("Failed to load billing summary");
      }
      return res.json();
    },
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  // When the failure is an auth error, the global handler clears the
  // session and redirects to /login — don't show a stale failure label
  // in the meantime. Only "real" failures (e.g. 500) get the
  // "Couldn't load" + retry treatment.
  const isAuthFail = isError && isAuthErrorLike(error);
  const isRealFail = isError && !isAuthFail;
  const handleRetry = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void refetch();
  };

  const used = data?.creditsUsed ?? 0;
  const quota = data?.totalQuota;
  const booster = data?.boosterCreditsAvailable ?? 0;

  // Combine plan + booster into a single denominator so the user sees
  // one "X / Y" figure. Falls back to whichever side is present.
  const combinedTotal =
    quota != null || booster > 0
      ? Number(quota ?? 0) + Number(booster ?? 0)
      : null;

  const percent =
    combinedTotal != null && combinedTotal > 0
      ? Math.min(100, Math.max(0, Math.round((Number(used) / combinedTotal) * 100)))
      : 0;
  const isOver = percent >= 100;
  const isWarn = percent >= 80;

  const hasAnyCredits = data?.hasPlan || booster > 0;

  const baseClass =
    variant === "sidebar"
      ? "flex w-full items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium hover:bg-secondary transition-colors text-left"
      : variant === "mobile-compact"
        ? "flex min-w-0 flex-1 items-center text-left hover:opacity-90 transition-opacity"
        : "inline-flex items-center gap-2 h-11 min-w-[44px] px-3 sm:px-4 rounded-lg border border-border hover:bg-secondary transition-colors text-sm font-medium";

  return (
    <Link
      href="/billing"
      className={cn(
        baseClass,
        isOver && "text-red-500",
        !isOver && isWarn && "text-amber-500",
      )}
      aria-label="View credit usage and billing"
      data-testid="credit-usage-counter"
    >
      {variant !== "mobile-compact" && (
        <CreditCard size={variant === "sidebar" ? 18 : 16} className="shrink-0" />
      )}
      {variant === "mobile-compact" ? (
        <span
          className="truncate text-xs font-medium leading-tight"
          data-testid="credit-usage-mobile-text"
        >
          {isLoading
            ? "Loading credits…"
            : isAuthFail
              ? null
              : isRealFail
                ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleRetry}
                    title="Couldn't load credit usage — tap to retry"
                    className="text-muted-foreground underline decoration-dotted underline-offset-2"
                    data-testid="credit-usage-retry-mobile"
                  >
                    {isFetching ? "Retrying credits…" : "Couldn't load credits — tap to retry"}
                  </span>
                )
                : data?.hasPlan && combinedTotal != null
                  ? <>{formatNumber(used)} / {formatNumber(combinedTotal)} credits used</>
                  : `${formatNumber(used)} credits used`}
        </span>
      ) : variant === "sidebar" ? (
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">Credit Usage</span>
            {hasAnyCredits && combinedTotal != null && (
              <span className="text-[10px] text-muted-foreground">{percent}%</span>
            )}
          </div>
          <div className="mt-0.5 truncate text-sm font-semibold">
            {isLoading
              ? "…"
              : isAuthFail
                ? "…"
                : isRealFail
                  ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={handleRetry}
                      title="Couldn't load credit usage — click to retry"
                      className="text-muted-foreground text-xs font-medium underline decoration-dotted underline-offset-2"
                      data-testid="credit-usage-retry-sidebar"
                    >
                      {isFetching ? "Retrying…" : "Couldn't load — retry"}
                    </span>
                  )
                  : hasAnyCredits && combinedTotal != null
                    ? `${formatNumber(used)} / ${formatNumber(combinedTotal)}`
                    : `${formatNumber(used)} used`}
          </div>
          {hasAnyCredits && combinedTotal != null && (
            <div className="mt-1.5 h-1 w-full rounded-full bg-secondary overflow-hidden" aria-hidden="true">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  isOver ? "bg-red-500" : isWarn ? "bg-amber-500" : "bg-primary",
                )}
                style={{ width: `${percent}%` }}
              />
            </div>
          )}
        </div>
      ) : (
        <span className="hidden sm:inline">
          {isLoading
            ? "…"
            : isAuthFail
              ? "…"
              : isRealFail
                ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleRetry}
                    title="Couldn't load credit usage — click to retry"
                    className="text-muted-foreground underline decoration-dotted underline-offset-2"
                    data-testid="credit-usage-retry-top"
                  >
                    {isFetching ? "Retrying…" : "Couldn't load — retry"}
                  </span>
                )
                : hasAnyCredits && combinedTotal != null
                  ? `${formatNumber(used)} / ${formatNumber(combinedTotal)}`
                  : `${formatNumber(used)} credits`}
        </span>
      )}
    </Link>
  );
}
